import { useMemo, useState, type FormEvent } from "react";
import type { QuoteView } from "@contracts/index";
import { computeTotals } from "@domain/quote";
import { formatMoney } from "@presentation/format";
import { labelsFor } from "@presentation/labels";
import { errorMessage } from "../lib/api";
import { clientApi } from "../lib/client-api";

/**
 * Lets the client pick optional items (with a live total), sign with name and
 * job title, and accept. Totals use the same domain function as the server.
 */
export function AcceptPanel({ quote, onAccepted }: { quote: QuoteView; onAccepted: (q: QuoteView) => void }) {
  const t = labelsFor(quote.language);
  const optional = quote.items.filter((i) => i.optional);
  const [selected, setSelected] = useState<Set<string>>(() => new Set(optional.filter((i) => i.selected).map((i) => i.id)));
  const [name, setName] = useState("");
  const [position, setPosition] = useState("");
  const [confirmed, setConfirmed] = useState(false);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const totals = useMemo(
    () =>
      computeTotals(
        quote.items.map((i) => (i.optional ? { ...i, selected: selected.has(i.id) } : i)),
        quote.taxMode,
        quote.currency,
      ),
    [quote, selected],
  );

  function toggle(id: string) {
    setSelected((current) => {
      const next = new Set(current);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  async function onSubmit(event: FormEvent) {
    event.preventDefault();
    setSending(true);
    setError(null);
    try {
      onAccepted(await clientApi.accept(quote.id, { name, position, selectedOptionalItemIds: [...selected] }));
    } catch (e) {
      setError(errorMessage(e));
    } finally {
      setSending(false);
    }
  }

  return (
    <form className="card accept-panel" onSubmit={onSubmit}>
      <h2>{t.acceptTitle}</h2>
      <p className="muted">{t.acceptIntro}</p>
      {optional.length > 0 && (
        <fieldset>
          <legend>{t.chooseOptions}</legend>
          {optional.map((item) => (
            <label key={item.id} className="inline option">
              <input type="checkbox" checked={selected.has(item.id)} onChange={() => toggle(item.id)} />
              {item.concept} · {formatMoney(Math.round(item.unitPriceCents * item.quantity), quote.currency, quote.language)}
            </label>
          ))}
        </fieldset>
      )}
      <p className="accept-total">
        {t.total} {quote.currency}: <strong>{formatMoney(totals.totalCents, quote.currency, quote.language)}</strong>
      </p>
      <div className="grid">
        <label>
          {t.yourName}
          <input required value={name} onChange={(e) => setName(e.target.value)} autoComplete="name" />
        </label>
        <label>
          {t.yourPosition}
          <input required value={position} onChange={(e) => setPosition(e.target.value)} autoComplete="organization-title" />
        </label>
      </div>
      <label className="inline">
        <input type="checkbox" required checked={confirmed} onChange={(e) => setConfirmed(e.target.checked)} />
        {t.confirmAccept}
      </label>
      {error && <p className="error">{error}</p>}
      <div className="actions">
        <button className="primary" type="submit" disabled={sending || !confirmed}>
          {sending ? t.accepting : t.acceptButton}
        </button>
      </div>
    </form>
  );
}
